import { useState, useEffect, useCallback, useRef } from 'react';
import {
  audioRecordingDB,
  IAudioRecording,
  coachingDB,
  uploadObservationAudio,
  SyncRecordStatus,
  ObservationStatus,
  userDB,
  updateToken,
  getPresignedUploadUrls,
  uploadFileToS3,
} from '@taleemabad/db';
import { tlLogger } from '../utils/logger';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Network } from '@capacitor/network';
import { buildUploadBlob } from '../utils/audio-sync-utils';
import {
  COACHING_SYNC_CONFIG,
  COACHING_RECORDING_CONFIG,
} from '../utils/coaching-constants';

export enum SyncStatus {
  Idle = 'idle',
  Syncing = 'syncing',
  Error = 'error',
}

interface AudioSyncService {
  syncStatus: SyncStatus;
  pendingCount: number;
  failedCount: number;
  isOnline: boolean;
  uploadProgressMap: Record<string, { progress: number; step?: string; details?: string }>;
  triggerSync: (referenceId?: string | number) => Promise<void>;
}

const SYNC_INTERVAL_MS = COACHING_SYNC_CONFIG.SYNC_INTERVAL_MS;

// GLOBAL SHARED LOCK: Prevents duplicate syncs across multiple hook instances
let globalIsSyncing = false;
let globalSyncRequested = false;

// DEDUPLICATION CACHE: Prevents redundant API calls for the same observation within a sync cycle
const activePresignRequests = new Map<string, Promise<any>>();

/**
 * Backgrounds services to sync audio recordings with backend
 */
export const useAudioSyncService = (): AudioSyncService => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(SyncStatus.Idle);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [isOnline, setIsOnline] = useState(true);
  const [uploadProgressMap, setUploadProgressMap] = useState<
    Record<string, { progress: number; step?: string; details?: string }>
  >({});

  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recentlyRetriedIds = useRef<Set<string>>(new Set());

  // Failure backoff to prevent CPU storms on endpoint errors (like 404s)
  const lastFailureTime = useRef<number>(0);
  const FAILURE_BACKOFF_MS = 30000; // 30 seconds

  // Monitor network status
  useEffect(() => {
    const isNative = Capacitor.getPlatform() !== 'web';

    if (isNative) {
      let active = true;
      let listenerHandle: { remove: () => Promise<void> } | null = null;

      const setupNetworkListener = async () => {
        const status = await Network.getStatus();
        if (!active) return;
        setIsOnline(status.connected);

        listenerHandle = await Network.addListener(
          'networkStatusChange',
          (status) => {
            setIsOnline(status.connected);
            if (status.connected && !globalIsSyncing) {
              triggerSync();
            }
          },
        );
      };

      setupNetworkListener();

      return () => {
        active = false;
        if (listenerHandle) {
          listenerHandle.remove();
        }
      };
    } else {
      const handleOnline = () => {
        setIsOnline(true);
        triggerSync();
      };
      const handleOffline = () => setIsOnline(false);

      setIsOnline(navigator.onLine);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  // Update counts with state-change guards
  const updateCounts = useCallback(async () => {
    const pending = await audioRecordingDB.getAllPendingRecordings();
    const failed = await audioRecordingDB.getAllFailedRecordings();

    setPendingCount(prev => prev !== pending.length ? pending.length : prev);
    setFailedCount(prev => prev !== failed.length ? failed.length : prev);
  }, []);

  /**
   * Defensive Cleanup Logic
   */
  const performStorageCleanup = useCallback(async () => {
    if (!COACHING_SYNC_CONFIG.CLEANUP_LOCAL_AUDIO_AFTER_SYNC) return;

    try {
      const allRecordings = await audioRecordingDB.audioRecordings.toArray();

      for (const recording of allRecordings) {
        if (!recording.id || !recording.referenceId) continue;
        if (['recording', 'syncing'].includes(recording.status)) continue;

        const observation = await coachingDB.observations.get(
          recording.referenceId.toString(),
        );

        if (observation?.audioUrl) {
          if (Capacitor.getPlatform() !== 'web' && recording.fileName) {
            try {
              await Filesystem.deleteFile({
                path: recording.fileName,
                directory: Directory.Data,
              });
            } catch (err) {
              tlLogger.error('AudioSync:Cleanup:DeleteFileFailed', { error: err });
            }
            try {
              const cacheFiles = await Filesystem.readdir({
                path: '',
                directory: Directory.Cache,
              });
              for (const file of cacheFiles.files) {
                if (
                  file.name.startsWith(`tl_merge_output_${recording.id}_`) &&
                  file.name.endsWith('.mp4')
                ) {
                  await Filesystem.deleteFile({
                    path: file.name,
                    directory: Directory.Cache,
                  });
                }
              }
            } catch (err) {
              tlLogger.error('AudioSync:Cleanup:DeleteCacheFailed', { error: err });
            }
          }
          await audioRecordingDB.deleteRecording(recording.id);
        }
      }
    } catch (error) {
      tlLogger.error('AudioSync:Cleanup:Failed', { error });
    }
  }, []);

  // Main sync function
  const syncRecordings = useCallback(
    async (forcedId?: string | number) => {
      // BACKOFF CHECK: Skip if failed recently
      if (!forcedId && Date.now() - lastFailureTime.current < FAILURE_BACKOFF_MS) {
        return;
      }

      // STRICT ATOMIC LOCK
      if (globalIsSyncing) {
        globalSyncRequested = true;
        return;
      }
      globalIsSyncing = true;

      try {
        const networkStatus = await Network.getStatus();
        const isActuallyOnline = networkStatus.connected && navigator.onLine;

        if (!isActuallyOnline) {
          globalIsSyncing = false;
          setSyncStatus(prev => prev !== SyncStatus.Idle ? SyncStatus.Idle : prev);
          return;
        }

        setSyncStatus(prev => prev !== SyncStatus.Syncing ? SyncStatus.Syncing : prev);
        tlLogger.log('AudioSync:LoopStarted', { forcedId });

        // CLEANUP STALE
        try {
          await audioRecordingDB.cleanupStaleRecordings();
        } catch (err) {
          tlLogger.error('AudioSync:CleanupStaleFailed', { error: err });
        }

        if (forcedId) {
          try {
            const rec = await audioRecordingDB.getRecordingByReferenceId(forcedId);
            if (rec?.id) {
              await audioRecordingDB.updateStatus(rec.id, 'pending');
              await audioRecordingDB.updateStatus(rec.id, 'syncing');
            }
          } catch (e) {
            tlLogger.error('AudioSync:ForcedIdStatusUpdateFailed', { error: e });
          }
        }

        let syncable = await audioRecordingDB.getSyncableRecordings();

        if (forcedId) {
          const forcedRecording = await audioRecordingDB.getRecordingByReferenceId(forcedId);
          if (forcedRecording && forcedRecording.status !== 'synced' && !syncable.some((r) => r.id === forcedRecording.id)) {
            syncable = [forcedRecording, ...syncable];
          }
        }

        const recordings = syncable.filter((r) => {
          if (forcedId && r.referenceId?.toString() === forcedId.toString()) return true;
          if (r.status !== 'failed' || !r.lastSyncAttempt) return true;
          const retryDelayMs = Math.min(Math.pow(2, r.retryCount || 0) * 15 * 1000, 10 * 60 * 1000);
          return Date.now() >= (r.lastSyncAttempt + retryDelayMs);
        });

        if (recordings.length === 0) {
          setSyncStatus(prev => prev !== SyncStatus.Idle ? SyncStatus.Idle : prev);
          globalIsSyncing = false;
          return;
        }

        for (const recording of recordings) {
          if (!recording.id || !recording.referenceId) continue;

          try {
            const isForced = forcedId && recording.referenceId?.toString() === forcedId.toString();
            let observation = await coachingDB.getObservationById(recording.referenceId.toString());

            if ((!recording.blob || recording.blob.size === 0) && (!recording.priorBlobs || recording.priorBlobs.length === 0)) {
              await audioRecordingDB.deleteRecording(recording.id);
              continue;
            }

            if (!observation) throw new Error('Observation not found');

            const status = String(observation?.status || '').toLowerCase();
            const shouldSkip = !isForced && (status === 'in_progress' || status === 'pending_multimedia');
            if (shouldSkip) continue;

            await audioRecordingDB.updateStatus(recording.id, 'syncing');

            // PHOTO SYNC (Resumable)
            let imagesS3Urls = observation.imagesS3Urls || [];
            const localPhotos = await coachingDB.observationPhotos.where('observationId').equals(observation.id).toArray();

            if (localPhotos.length > 0 && imagesS3Urls.length < localPhotos.length) {
              const startIndex = imagesS3Urls.length;
              const remainingCount = localPhotos.length - startIndex;
              const { urls } = await getPresignedUploadUrls(observation.id, remainingCount);
              const uploadedUrls: string[] = [...imagesS3Urls];
              for (let i = 0; i < remainingCount; i++) {
                const photo = localPhotos[startIndex + i];
                const s3Info = urls[i];
                let blob: Blob;
                if (Capacitor.getPlatform() === 'web' && photo.blob) blob = photo.blob;
                else {
                  const fileResult = await Filesystem.readFile({ path: photo.localUrl });
                  const response = await fetch(`data:image/jpeg;base64,${fileResult.data}`);
                  blob = await response.blob();
                }
                await uploadFileToS3(s3Info.uploadUrl, blob, 'image/jpeg');
                uploadedUrls.push(s3Info.finalUrl);
                // Persist S3 URL immediately in IndexedDB to support background resumption
                await coachingDB.updateObservation(observation.id, { imagesS3Urls: [...uploadedUrls] });
              }
              observation.imagesS3Urls = uploadedUrls;
            }

            const uploadBlob = await buildUploadBlob(recording);
            await uploadObservationAudio(observation, uploadBlob, recording.fileName, (pe) => {
              if (pe.total) {
                const progress = Math.round((pe.loaded * 100) / pe.total);
                setUploadProgressMap((prev) => ({ ...prev, [recording.referenceId!]: { progress: 95 + Math.round(progress * 0.05), step: 'Submitting...' } }));
              }
            });

            await audioRecordingDB.updateStatus(recording.id, 'synced');
            await coachingDB.observations.update(observation.id, { syncStatus: SyncRecordStatus.Synced });

          } catch (err: any) {
            // SHIELD TECHNICAL ERRORS: Never show raw API messages to teachers
            let errorMsg = "We couldn't save your review. Please try again later.";

            if (err.response?.status === 404) {
              errorMsg = 'Feature temporarily paused for maintenance';
              lastFailureTime.current = Date.now();
            } else if (!err.response) {
              errorMsg = 'Connection lost. We will retry automatically.';
              lastFailureTime.current = Date.now();
            }

            await audioRecordingDB.updateStatus(recording.id, 'failed', errorMsg);
            break;
          }
        }

        await updateCounts();
        await performStorageCleanup();
        setSyncStatus(SyncStatus.Idle);

      } catch (error) {
        setSyncStatus(SyncStatus.Error);
      } finally {
        globalIsSyncing = false;
        if (globalSyncRequested) {
          globalSyncRequested = false;
          queueMicrotask(() => syncRecordings());
        }
      }
    },
    [updateCounts, performStorageCleanup],
  );

  const triggerSync = useCallback(
    async (referenceId?: string | number) => {
      await syncRecordings(referenceId);
    },
    [syncRecordings],
  );

  // RUN ONCE on mount
  useEffect(() => {
    const rescue = async () => {
      try {
        await audioRecordingDB.cleanupStaleRecordings();

        // SANITIZATION MIGRATOR: Wipe any old technical errors from the DB
        const failed = await audioRecordingDB.getAllFailedRecordings();
        for (const rec of failed) {
          if (rec.id && (rec.lastError?.includes('Request failed') || rec.lastError?.includes('status code'))) {
            await audioRecordingDB.updateStatus(rec.id, 'failed', "We couldn't save your review. Please try again later.");
          }
        }

        await updateCounts();
        await triggerSync();
        await performStorageCleanup();
      } catch (e) {
        tlLogger.error('AudioSync:RescueFailed', { error: e });
      }
    };
    rescue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic interval
  useEffect(() => {
    updateCounts();
    const intervalId = setInterval(() => {
      if (navigator.onLine) syncRecordings();
    }, SYNC_INTERVAL_MS);
    syncIntervalRef.current = intervalId;
    return () => clearInterval(intervalId);
  }, [syncRecordings, updateCounts]);

  return {
    syncStatus,
    pendingCount,
    failedCount,
    isOnline,
    uploadProgressMap,
    triggerSync,
  };
};
